/**
 * DisruptionConfigFields — the editable body of a {@link TrlDisruptionConfig}.
 *
 * Game-level only. Purely controlled: it renders `value` and reports every edit
 * through `onChange`.
 */

import type { TrlDisruptionConfig } from '@swtorbit/shared-socket-events';

interface DisruptionConfigFieldsProps {
  value: TrlDisruptionConfig;
  onChange: (next: TrlDisruptionConfig) => void;
  disabled?: boolean;
}

export function DisruptionConfigFields({ value, onChange, disabled = false }: DisruptionConfigFieldsProps) {
  return (
    <div className="d-flex flex-column gap-sm">
      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Disruption</span>

        <label className="d-flex align-items-center gap-2xs fs-sm">
          <input
            className="checkbox"
            type="checkbox"
            checked={value.enabled}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
          <span>
            <strong>Enable disruption</strong>
            <span className="fs-xs text-slate d-block">
              When on, a player from an opposing team who hits someone with Sparkle Powder while
              that person is scanning blocks their seismic scanner. A blocked scanner can&apos;t
              find anything until a teammate hits them with Sparkle Powder.
            </span>
          </span>
        </label>
      </div>

      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Rules</span>
        <span className="fs-xs text-slate">
          The hit only counts if it lands during the ~16 second scan — a scan cancelled before the
          hit, or a player who isn&apos;t scanning, is unaffected. Timing comes from the target&apos;s
          own combat log.
        </span>
        <span className="fs-xs text-slate">
          Each player can disrupt a given opponent only once per planet. A block lasts across
          planets until a teammate clears it; you can also clear blocks from the Teams tab.
        </span>
        <span className="fs-xs text-slate">
          Turning disruption off stops new disruptions but leaves existing blocks in place.
        </span>
      </div>
    </div>
  );
}
